import {useContext} from 'react'

import Context from '@s-ui/react-context'

import {getValueWithFinancingFormat} from '../utils.js'

export const useGetListOfFinancingConditions = ({financingData, isModal}) => {
  const {domain, i18n} = useContext(Context)

  const config = domain.get('config')
  const {FINANCING_PARTNERS, INSURANCE_IDS} = config.get('financing')
  const {
    BMW_BANK,
    COFIDIS_WITHOUT_INSURANCE,
    COFIDIS_WITH_LIFE_INSURANCE,
    COFIDIS_WITH_LIFE_PLUS_INSURANCE,
    NO_INSURANCE,
    SANTANDER_WITH_LIFE_INSURANCE,
    CASUALTY,
    CAIXA_WITHOUT_INSURANCE,
    CAIXA_WITH_LIFE_INSURANCE,
    CONFIA_WITH_LIFE_INSURANCE,
    CONFIA_WITHOUT_INSURANCE,
    SANTANDER_PRI_WITHOUT_INSURANCE,
    LENDROCK_WITHOUT_INSURANCE,
    PAYMENT_PROTECTION,
    VWFS_WITH_INSURANCE,
    MOBILIZE_WITH_LIFE_INSURANCE,
    STELLANTIS_WITH_CPI_INSURANCE,
    STELLANTIS_WITH_CPI_CASUALTY_INSURANCE,
    STELLANTIS_WITH_SC_INSURANCE,
    STELLANTIS_WITH_SC_INSURANCE_1,
    STELLANTIS_WITH_SC_GMP_SA_INSURANCE,
    WITHOUT_INSURANCE_BBVA,
    PPP_BBVA,
    MOBILIZE_NISSAN_WITH_LIFE_INSURANCE
  } = INSURANCE_IDS

  const {
    amountToFinance,
    capital,
    cashPrice,
    computedPrice,
    entry,
    fee,
    financedPrice,
    firstFee,
    loanRequestAvailable = false,
    insuranceId,
    lender,
    licenseWithdrawalInsuranceAmount,
    lifeInsuranceAmount,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    tae,
    terms,
    tin,
    totalAmountDue,
    totalInsuranceAmount,
    totalLoanCost,
    totalTermsAmount,
    vehiclePrice
  } = financingData

  // the key is the insuranceId
  const insuranceInfoById = {
    [BMW_BANK]: {label: '', extraLabel: 'FINANCING_INFO.OPTIONAL', value: lifeInsuranceAmount},
    [COFIDIS_WITHOUT_INSURANCE]: {label: '', extraLabel: 'FINANCING_INFO.OPTIONAL', value: lifeInsuranceAmount},
    [COFIDIS_WITH_LIFE_INSURANCE]: {label: '', extraLabel: 'FINANCING_INFO.OPTIONAL', value: lifeInsuranceAmount},
    [COFIDIS_WITH_LIFE_PLUS_INSURANCE]: {label: '', extraLabel: 'FINANCING_INFO.OPTIONAL', value: lifeInsuranceAmount},
    [CONFIA_WITH_LIFE_INSURANCE]: {
      label: 'FINANCING_INFO.CONFIA_INFO.PROTECTION_INSURANCE',
      value: lifeInsuranceAmount
    },
    [CONFIA_WITHOUT_INSURANCE]: {label: '', extraLabel: 'FINANCING_INFO.OPTIONAL', value: lifeInsuranceAmount},
    [NO_INSURANCE]: {label: '', extraLabel: 'FINANCING_INFO.OPTIONAL', value: lifeInsuranceAmount},
    [SANTANDER_WITH_LIFE_INSURANCE]: {
      label: 'FINANCING_INFO.LIFE_INSURANCE_PLUS',
      extraLabel: 'FINANCING_INFO.OPTIONAL',
      value: lifeInsuranceAmount
    },
    [CASUALTY]: {
      label: 'FINANCING_INFO.CASUALTY_AND_LIFE_INSURANCE',
      extraLabel: 'FINANCING_INFO.OPTIONAL',
      value: totalInsuranceAmount
    },
    [CAIXA_WITHOUT_INSURANCE]: {label: '', extraLabel: 'FINANCING_INFO.OPTIONAL', value: lifeInsuranceAmount},
    [CAIXA_WITH_LIFE_INSURANCE]: {
      label: 'FINANCING_INFO.LIFE_INSURANCE',
      extraLabel: 'FINANCING_INFO.OPTIONAL',
      value: lifeInsuranceAmount
    },
    [SANTANDER_PRI_WITHOUT_INSURANCE]: {label: '', extraLabel: 'FINANCING_INFO.OPTIONAL', value: lifeInsuranceAmount},
    [LENDROCK_WITHOUT_INSURANCE]: {label: '', extraLabel: 'FINANCING_INFO.OPTIONAL', value: lifeInsuranceAmount},
    [PAYMENT_PROTECTION]: {
      label: 'FINANCING_INFO.PROTECTION_INSURANCE',
      extraLabel: 'FINANCING_INFO.OPTIONAL',
      value: lifeInsuranceAmount
    },
    [VWFS_WITH_INSURANCE]: {
      label: 'FINANCING_INFO.LIFE_AND_UNEMPLOYMENT_INSURANCE',
      extraLabel: 'FINANCING_INFO.OPTIONAL',
      value: lifeInsuranceAmount
    },
    [MOBILIZE_WITH_LIFE_INSURANCE]: {
      label: 'FINANCING_INFO.PROTECTION_INSURANCE',
      extraLabel: 'COMMON.MANDATORY',
      value: lifeInsuranceAmount
    },
    [STELLANTIS_WITH_CPI_INSURANCE]: {
      label: 'FINANCING_INFO.LOAN_INSURANCE',
      extraLabel: 'FINANCING_INFO.OPTIONAL',
      value: lifeInsuranceAmount
    },
    [STELLANTIS_WITH_SC_GMP_SA_INSURANCE]: {
      label: 'FINANCING_INFO.LOAN_INSURANCE',
      extraLabel: 'FINANCING_INFO.OPTIONAL',
      value: lifeInsuranceAmount
    },
    [STELLANTIS_WITH_CPI_CASUALTY_INSURANCE]: {
      label: 'FINANCING_INFO.LOAN_INSURANCE',
      extraLabel: 'FINANCING_INFO.OPTIONAL',
      value: lifeInsuranceAmount
    },
    [STELLANTIS_WITH_SC_INSURANCE]: {
      label: 'FINANCING_INFO.LOAN_INSURANCE',
      extraLabel: 'FINANCING_INFO.OPTIONAL',
      value: lifeInsuranceAmount
    },
    [STELLANTIS_WITH_SC_INSURANCE_1]: {
      label: 'FINANCING_INFO.LOAN_INSURANCE',
      extraLabel: 'FINANCING_INFO.OPTIONAL',
      value: lifeInsuranceAmount
    },
    [WITHOUT_INSURANCE_BBVA]: {label: '', extraLabel: 'FINANCING_INFO.OPTIONAL', value: lifeInsuranceAmount},
    [PPP_BBVA]: {
      label: 'FINANCING_INFO.PROTECTION_INSURANCE',
      extraLabel: 'FINANCING_INFO.OPTIONAL',
      value: lifeInsuranceAmount
    },
    [MOBILIZE_NISSAN_WITH_LIFE_INSURANCE]: {
      label: 'FINANCING_INFO.PROTECTION_INSURANCE',
      extraLabel: 'COMMON.MANDATORY',
      value: lifeInsuranceAmount
    }
  }

  const isFdaCalculatorB2cAvailable = loanRequestAvailable
  const adPrice = financedPrice || cashPrice
  const financedPriceFda = computedPrice || adPrice
  const hasNotDiscount = financedPriceFda === cashPrice
  const checkIfNegative = number => number <= 0

  // Check if lender is one of the following
  const isBmwBankLender = FINANCING_PARTNERS.BMW_BANK === lender
  const isLaCaixaLender = FINANCING_PARTNERS.CAIXA === lender
  const isSantanderPriLender = FINANCING_PARTNERS.SANTANDER_PRI === lender
  const isConfiaLender = FINANCING_PARTNERS.CONFIA === lender
  const isVWFSLender = FINANCING_PARTNERS.VWFS === lender

  // Check if insurance is one of the following

  // Check if amount is not negative or exists
  const checkIfAmountIsNotNegative = amount => Boolean(amount) && amount >= 0
  const minEntryExists = minEntry !== undefined && minEntry !== null

  const instalmentLabelByInsuranceId = {
    [SANTANDER_PRI_WITHOUT_INSURANCE]: 'COMMON.FEE_MONTHLY_PERSONAL_LOAN',
    [CONFIA_WITH_LIFE_INSURANCE]: 'FINANCING_INFO.CONFIA_INFO.FEE_MONTHLY',
    [CONFIA_WITHOUT_INSURANCE]: 'FINANCING_INFO.CONFIA_INFO.FEE_MONTHLY'
  }
  const openingExpensesLabelByInsuranceId = {
    [CONFIA_WITH_LIFE_INSURANCE]: 'FINANCING_INFO.CONFIA_INFO.OPENING_FEE',
    [CONFIA_WITHOUT_INSURANCE]: 'FINANCING_INFO.CONFIA_INFO.OPENING_FEE'
  }

  const instalmentLabel = i18n.t(instalmentLabelByInsuranceId[insuranceId] ?? 'COMMON.FEE_MONTHLY')
  const openingExpensesLabel = i18n.t(openingExpensesLabelByInsuranceId[insuranceId] ?? 'FINANCING_INFO.OPENING_FEE')

  const minEntryLabel = isSantanderPriLender
    ? i18n.t('FINANCING_INFO.CASH_PAYMENT')
    : i18n.t('FINANCING_INFO.ENTRANCE_FEE')

  const listFinanceConditions = [
    {
      id: 'instalment',
      value: getValueWithFinancingFormat(fee),
      labelText: `${instalmentLabel}*`,
      isVisible: isModal && checkIfAmountIsNotNegative(fee)
    },
    {
      id: 'vehiclePrice',
      value: getValueWithFinancingFormat(vehiclePrice),
      labelText: i18n.t('FINANCING_INFO.TOTAL_PRICE_VEHICLE'),
      isVisible: !isSantanderPriLender && checkIfAmountIsNotNegative(vehiclePrice)
    },
    {
      id: 'cashPrice',
      value: getValueWithFinancingFormat(cashPrice),
      // labelText: i18n.t('FINANCING_INFO.TOTAL_PRICE_VEHICLE'),
      labelText: 'Precio al contado',
      isVisible: isFdaCalculatorB2cAvailable && checkIfAmountIsNotNegative(cashPrice) && !hasNotDiscount
    },
    {
      id: 'promotionalDiscount',
      value:
        (entry !== undefined && entry > maxEntryToGetDiscount) || cashPrice < financedPriceFda
          ? getValueWithFinancingFormat(0)
          : getValueWithFinancingFormat(financedPriceFda - cashPrice),
      labelText: 'Descuento promocional',
      isVisible: isFdaCalculatorB2cAvailable && !hasNotDiscount && checkIfNegative(financedPriceFda - cashPrice)
    },
    {
      id: 'computedPrice',
      value: getValueWithFinancingFormat(financedPriceFda),
      labelText: i18n.t('FINANCING_INFO.FINANCED_PRICE'),
      isVisible: !isSantanderPriLender && checkIfAmountIsNotNegative(financedPriceFda)
    },
    {
      id: 'capital',
      value: getValueWithFinancingFormat(capital),
      labelText: i18n.t('FINANCING_INFO.REQUESTED_AMOUNT'),
      isVisible: isSantanderPriLender && checkIfAmountIsNotNegative(capital)
    },
    {
      id: 'minEntry',
      value:
        !isSantanderPriLender && entry !== undefined
          ? getValueWithFinancingFormat(entry)
          : getValueWithFinancingFormat(minEntry),
      labelText: minEntryLabel,
      isVisible: (!isSantanderPriLender || isModal) && minEntryExists
    },
    {
      id: 'terms',
      value: terms,
      labelText: i18n.t('COMMON.PLURALS.MONTH', 2),
      isVisible: Boolean(terms) && !isSantanderPriLender
    },
    {
      id: 'financingAmount',
      value: getValueWithFinancingFormat(amountToFinance),
      labelText: i18n.t('FINANCING_INFO.FINANCED_AMOUNT'),
      isVisible: isBmwBankLender && checkIfAmountIsNotNegative(amountToFinance)
    },
    {
      id: 'totalAmountDue',
      value: getValueWithFinancingFormat(totalAmountDue),
      labelText: 'Precio total a plazos',
      isVisible: isBmwBankLender && Boolean(totalAmountDue)
    },
    {
      highlightedValue: isLaCaixaLender,
      id: 'lifeInsuranceAmount',
      value: getValueWithFinancingFormat(insuranceInfoById[insuranceId]?.value),
      labelText: i18n.t(insuranceInfoById[insuranceId]?.label ?? ''),
      labelExtraText: i18n.t(insuranceInfoById[insuranceId]?.extraLabel ?? ''),
      isVisible: Boolean(insuranceInfoById[insuranceId]?.label) && Boolean(insuranceInfoById[insuranceId]?.value)
    },
    {
      id: 'licenseWithdrawalInsuranceAmount',
      value: getValueWithFinancingFormat(licenseWithdrawalInsuranceAmount),
      labelText: i18n.t('FINANCING_INFO.WITHDRAWAL_INSURANCE'),
      labelExtraText: `(${i18n.t('FINANCING_INFO.OPTIONAL')})`,
      isVisible: isVWFSLender && Boolean(licenseWithdrawalInsuranceAmount)
    },
    {
      id: 'totalLoanCost',
      value: getValueWithFinancingFormat(totalLoanCost),
      labelText: i18n.t('FINANCING_INFO.TOTAL_LOAN_AMOUNT'),
      isVisible: isVWFSLender && Boolean(totalLoanCost)
    },
    {
      id: 'totalAmountTerms',
      value: getValueWithFinancingFormat(totalTermsAmount),
      labelText: i18n.t('FINANCING_INFO.TOTAL_AMOUNT_TERMS'),
      isVisible: Boolean(totalTermsAmount) && !isSantanderPriLender && !isBmwBankLender
    },
    {
      id: 'openingExpenses',
      value: getValueWithFinancingFormat(openingExpenses),
      labelText: openingExpensesLabel,
      isVisible: Boolean(openingExpenses)
    },
    {
      id: 'tin',
      value: !isNaN(tin) && i18n.f('percentage', tin),
      labelText: i18n.t('FINANCING_INFO.TIN'),
      isVisible: Boolean(tin)
    },
    {
      id: 'tae',
      value: !isNaN(tae) && i18n.f('percentage', tae),
      labelText: i18n.t('FINANCING_INFO.TAE'),
      isVisible: Boolean(tae),
      isBold: true
    },
    {
      id: 'totalAmountDue1',
      value: getValueWithFinancingFormat(totalAmountDue),
      labelText: i18n.t('FINANCING_INFO.TOTAL_AMOUNT_DUE'),
      isVisible: isSantanderPriLender && Boolean(totalAmountDue)
    },
    {
      id: 'firstInstallment',
      value: getValueWithFinancingFormat(firstFee),
      labelText: i18n.t('FINANCING_INFO.FIRST_INSTALLMENT'),
      isVisible: !isSantanderPriLender && !isConfiaLender && Boolean(firstFee)
    }
  ]

  return listFinanceConditions
}
